import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useToast,
  Card,
  CardBody,
  Heading,
  useColorModeValue,
  Spinner,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  IconButton,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Textarea,
  Collapse,
} from '@chakra-ui/react'
import { SearchIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, AddIcon, CheckIcon, CloseIcon, NotAllowedIcon } from '@chakra-ui/icons'
import { useBexco, BexcoQueryLog } from '../contexts/BexcoContext'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

const QueryLogsPage: React.FC = () => {
  const { queryLogs, setQueryLogs, isLoading, setIsLoading } = useBexco()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedLog, setSelectedLog] = useState<BexcoQueryLog | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [buildingFilter, setBuildingFilter] = useState('')
  const [validationFilter, setValidationFilter] = useState('')  // 🆕 v10.3: 검증 상태 필터
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [excludeIps, setExcludeIps] = useState('')  // 🆕 v10.10: 제외 IP 목록
  const [showExcludeIps, setShowExcludeIps] = useState(false)  // 🆕 제외 IP 입력창 표시 여부
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(10) // 페이지당 항목 수
  const [stats, setStats] = useState<any>(null)
  const toast = useToast()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  
  // 건물 목록
  const buildings = ['1전시장', '2전시장', '컨벤션홀', '오디토리움', '누리마루APEC하우스']
  
  // 🆕 v10.3: 검증 상태 목록
  const validationTypes = [
    { value: 'NONE', label: '정상', color: 'green' },
    { value: 'NO_ANSWER', label: '정보없음', color: 'orange' },
    { value: 'FORMAT', label: '출력형식', color: 'yellow' },
    { value: 'IRRELEVANT', label: '관련성없음', color: 'purple' },
    { value: 'WRONG', label: '오답', color: 'red' },
    { value: 'COMMUNICATION', label: '통신오류', color: 'gray' }
  ]
  
  // 검색어 debounce: 입력 멈춘 후 500ms 뒤에 검색 실행
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1) // 검색 시 첫 페이지로
    }, 500)
    
    return () => clearTimeout(timer)
  }, [searchTerm])

  // 쿼리 로그 데이터 로드
  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      })
      
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm)
      if (statusFilter) params.append('status', statusFilter)
      if (buildingFilter) params.append('building', buildingFilter)
      if (validationFilter) params.append('validationType', validationFilter)  // 🆕 v10.3
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      // 🆕 v10.10: 제외 IP 목록 (줄바꿈/쉼표로 구분된 IP들을 쉼표로 연결)
      if (excludeIps.trim()) {
        const ips = excludeIps.split(/[\n,]/).map(ip => ip.trim()).filter(ip => ip)
        if (ips.length > 0) {
          params.append('excludeIps', ips.join(','))
        }
      }
      
      //console.log('[QueryLogs] Fetching logs with params:', params.toString())
      
      const response = await fetch(`/graph/logs?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      })
      
      //console.log('[QueryLogs] Response status:', response.status)
      
      if (response.ok) {
        const result = await response.json()
        //console.log('[QueryLogs] Received data:', result)
        
        // 백엔드 응답 구조: {data: [...], pagination: {...}} 또는 {logs: [...], pagination: {...}}
        const logs = result.data || result.logs || []
        //console.log('[QueryLogs] Extracted logs:', logs)
        
        setQueryLogs(logs)
        setTotalPages(result.pagination?.totalPages || 1)
        setTotalItems(result.pagination?.totalItems || 0)
      } else {
        throw new Error('쿼리 로그 로드 실패')
      }
    } catch (error) {
      console.error('[QueryLogs] Failed to load logs:', error)
      toast({
        title: '오류',
        description: '쿼리 로그를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, pageSize, debouncedSearchTerm, statusFilter, buildingFilter, validationFilter, startDate, endDate, excludeIps, setQueryLogs, setIsLoading, toast])
  
  // 통계 데이터 로드
  // 🆕 v10.11: excludeIps 파라미터 추가
  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (buildingFilter) params.append('building', buildingFilter)
      if (startDate) params.append('startDate', startDate)
      if (endDate) params.append('endDate', endDate)
      // 🆕 v10.11: 제외 IP 목록 추가
      if (excludeIps.trim()) {
        const ips = excludeIps.split(/[\n,]/).map(ip => ip.trim()).filter(ip => ip)
        if (ips.length > 0) {
          params.append('excludeIps', ips.join(','))
        }
      }
      
      //console.log('[QueryLogs] Fetching stats with params:', params.toString())
      
      const response = await fetch(`/graph/logs/stats?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      })
      
      if (response.ok) {
        const result = await response.json()
        //console.log('[QueryLogs] Received stats:', result)
        setStats(result.data)
      }
    } catch (error) {
      console.error('[QueryLogs] Failed to load stats:', error)
    }
  }, [buildingFilter, startDate, endDate, excludeIps])
  
  // 쿼리 로그 데이터 로드 트리거
  useEffect(() => {
    loadLogs()
  }, [loadLogs])
  
  // 통계 데이터 로드 트리거
  useEffect(() => {
    loadStats()
  }, [loadStats])

  const handleViewLog = (log: BexcoQueryLog) => {
    setSelectedLog(log)
    onOpen()
  }

  // FAQ로 추가
  const handleAddToFaq = async (log: BexcoQueryLog) => {
    try {
      const response = await fetch(`/graph/logs/${log.id}/to-faq`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: '일반'
        })
      })

      if (response.ok) {
        toast({
          title: 'FAQ 추가 완료',
          description: '해당 질문과 답변이 FAQ에 추가되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
      } else {
        throw new Error('FAQ 추가 실패')
      }
    } catch (error) {
      console.error('[QueryLogs] Failed to add to FAQ:', error)
      toast({
        title: '오류',
        description: 'FAQ 추가 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // 🆕 v10.3: 검증 상태 업데이트
  const handleUpdateValidation = async (logId: number, validationType: string, validationReason?: string) => {
    try {
      const response = await fetch(`/api/validation/logs/${logId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          validationType,
          validationReason
        })
      })

      if (response.ok) {
        toast({
          title: '검증 상태 업데이트',
          description: `검증 상태가 "${getValidationText(validationType)}"으로 변경되었습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        // 로그 목록 새로고침
        loadLogs()
      } else {
        throw new Error('검증 상태 업데이트 실패')
      }
    } catch (error) {
      console.error('[QueryLogs] Failed to update validation:', error)
      toast({
        title: '오류',
        description: '검증 상태 업데이트 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // 🆕 v10.3: 검증된 QA로 등록 (정상 확정) - 키워드 기반 중복 체크
  const handleConfirmAsVerified = async (log: BexcoQueryLog) => {
    try {
      const response = await fetch('/api/validation/verified-qa', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: log.query_text,
          answer: log.response_text,
          sourceLogId: log.id,
          category: log.building || '일반',
          keyword: log.keyword || null  // 🔥 키워드 추가 (중복 체크용)
        })
      })

      if (response.ok) {
        const result = await response.json()
        
        // 🔥 중복 체크: 이미 등록된 경우
        if (result.data?.alreadyExists) {
          toast({
            title: 'ℹ️ 이미 등록됨',
            description: result.data.message || '이 QA는 이미 검증된 답변으로 등록되어 있습니다.',
            status: 'info',
            duration: 3000,
            isClosable: true,
          })
        } else {
          toast({
            title: '✅ 정상 확정 완료',
            description: '해당 QA가 검증된 답변으로 등록되었습니다.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
        onClose()
        loadLogs()
      } else {
        throw new Error('검증된 QA 등록 실패')
      }
    } catch (error) {
      console.error('[QueryLogs] Failed to confirm as verified:', error)
      toast({
        title: '오류',
        description: '검증된 QA 등록 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // 🆕 v10.3: 오답으로 마킹
  const handleMarkAsWrong = async (log: BexcoQueryLog) => {
    await handleUpdateValidation(log.id, 'WRONG', '관리자 확인 - 오답')
    onClose()
  }

  const getStatusColor = (status: string, isFaq?: boolean | number) => {
    // FAQ인 경우 보라색
    if (isFaq || status === 'FAQ') return 'purple'
    switch (status) {
      case 'SUCCESS': return 'green'
      case 'ERROR': return 'red'
      case 'NEEDS_IMPROVEMENT': return 'yellow'
      default: return 'gray'
    }
  }
  
  // 상태 텍스트 표시
  const getStatusText = (status: string, isFaq?: boolean | number) => {
    if (isFaq || status === 'FAQ') return 'FAQ'
    return status || 'UNKNOWN'
  }
  
  // 🆕 v10.3: 검증 상태 색상
  const getValidationColor = (validationType?: string) => {
    switch (validationType) {
      case 'NONE': return 'green'
      case 'NO_ANSWER': return 'orange'
      case 'FORMAT': return 'yellow'
      case 'IRRELEVANT': return 'purple'
      case 'WRONG': return 'red'
      case 'COMMUNICATION': return 'gray'
      default: return 'gray'
    }
  }
  
  // 🆕 v10.3: 검증 상태 텍스트
  const getValidationText = (validationType?: string) => {
    switch (validationType) {
      case 'NONE': return '정상'
      case 'NO_ANSWER': return '정보없음'
      case 'FORMAT': return '출력형식'
      case 'IRRELEVANT': return '관련성없음'
      case 'WRONG': return '오답'
      case 'COMMUNICATION': return '통신오류'
      default: return '-'
    }
  }
  
  // CSV 내보내기
  const handleExportCSV = () => {
    const headers = ['시간', '질문', '답변', '건물', '상태', '신뢰도', '처리시간', 'IP']
    const rows = queryLogs.map(log => [
      log.created_at || '',
      log.query_text || '',
      log.response_text || '',
      log.building || '',
      log.rag_status || '',
      log.confidence || '',
      log.processingTime || '',
      log.ip || ''
    ])
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `query_logs_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    
    toast({
      title: '내보내기 완료',
      description: 'CSV 파일이 다운로드되었습니다.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }
  
  // 필터 초기화
  const handleResetFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setBuildingFilter('')
    setValidationFilter('')  // 🆕 v10.3
    setStartDate('')
    setEndDate('')
    setExcludeIps('')  // 🆕 v10.10
    setShowExcludeIps(false)  // 🆕 v10.10
    setCurrentPage(1)
  }

  // 페이지 번호 클릭 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // 페이지당 항목 수 변경 핸들러
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1) // 페이지 크기 변경 시 첫 페이지로
  }

  // 표시할 페이지 번호 계산 (최대 5개 표시)
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxPagesToShow = 5
    
    if (totalPages <= maxPagesToShow) {
      // 전체 페이지가 5개 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // 현재 페이지를 중심으로 앞뒤 페이지 계산
      let startPage = Math.max(1, currentPage - 2)
      let endPage = Math.min(totalPages, currentPage + 2)
      
      // 시작이 1이 아니면 첫 페이지와 생략 표시
      if (startPage > 1) {
        pages.push(1)
        if (startPage > 2) {
          pages.push('...')
        }
      }
      
      // 중간 페이지들 (최대 5개)
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
      
      // 끝이 마지막 페이지가 아니면 생략 표시와 마지막 페이지
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...')
        }
        pages.push(totalPages)
      }
    }
    
    return pages
  }
  
  // 백엔드에서 필터링된 결과를 받으므로 클라이언트 사이드 필터링 불필요
  const filteredLogs = queryLogs

  if (isLoading && !queryLogs.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.500">쿼리 로그를 불러오는 중...</Text>
        </VStack>
      </Box>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* 페이지 헤더 */}
      <HStack justify="space-between">
        <Box>
          <Heading size="lg" color="blue.700" mb={2}>
            쿼리 로그 분석
          </Heading>
          <Text color="gray.600">
            사용자 질의와 AI 응답을 분석하여 시스템 성능을 개선하세요.
          </Text>
        </Box>
        {/*<Button
          leftIcon={<DownloadIcon />}
          colorScheme="blue"
          onClick={handleExportCSV}
          isDisabled={!queryLogs.length}
        >
          CSV 내보내기
        </Button>*/}
      </HStack>
      
      {/* 통계 카드 */}
      {stats && (
        <>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Stat>
                  <StatLabel>전체 쿼리</StatLabel>
                  <StatNumber>{totalItems}</StatNumber>
                  <StatHelpText>총 질문 수</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Stat>
                  <StatLabel>평균 신뢰도</StatLabel>
                  <StatNumber>
                    {stats.daily && stats.daily.length > 0
                      ? (stats.daily[0].avg_confidence * 100).toFixed(1) + '%'
                      : '-'}
                  </StatNumber>
                  <StatHelpText>가장 최근 날짜 평균</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Stat>
                  <StatLabel>평균 처리시간</StatLabel>
                  <StatNumber>
                    {stats.daily && stats.daily.length > 0
                      ? Math.round(stats.daily[0].avg_processing_time) + 'ms'
                      : '-'}
                  </StatNumber>
                  <StatHelpText>가장 최근 날짜 평균</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px" borderColor={borderColor}>
              <CardBody>
                <Stat>
                  <StatLabel>주요 건물</StatLabel>
                  <StatNumber fontSize="lg">
                    {stats.building && stats.building.length > 0
                      ? stats.building[0].building
                      : '-'}
                  </StatNumber>
                  <StatHelpText>
                    {stats.building && stats.building.length > 0
                      ? `${stats.building[0].count}건`
                      : '데이터 없음'}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>
          
          {/* 통계 차트 */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            {/* 시간대별 분포 */}
            {stats.hourly && stats.hourly.length > 0 && (
              <Card bg={cardBg} border="1px" borderColor={borderColor}>
                <CardBody>
                  <Heading size="sm" mb={4}>시간대별 쿼리 분포</Heading>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={stats.hourly}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="hour" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value + '시'}
                        label={{ value: '시간', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        allowDecimals={false}
                        label={{ value: '쿼리 수', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value: any) => [value + '건', '쿼리 수']}
                        labelFormatter={(label) => label + '시'}
                      />
                      <Bar dataKey="count" fill="#805AD5" radius={[4, 4, 0, 0]} name="쿼리 수" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}
            
            {/* 일별 추이 */}
            {stats.daily && stats.daily.length > 0 && (
              <Card bg={cardBg} border="1px" borderColor={borderColor}>
                <CardBody>
                  <Heading size="sm" mb={4}>최근 일별 쿼리 추이</Heading>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={stats.daily.slice(0, 7).map((item: any) => ({
                        ...item,
                        displayDate: new Date(item.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="displayDate" 
                        tick={{ fontSize: 12 }}
                        label={{ value: '날짜', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        allowDecimals={false}
                        label={{ value: '건수', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value: any) => [value + '건', '쿼리 수']}
                      />
                      <Bar dataKey="count" fill="#319795" radius={[4, 4, 0, 0]} name="쿼리 수" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}
          </SimpleGrid>
        </>
      )}

      {/* 필터 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="쿼리 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Select
                placeholder="상태 필터"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
                maxW="150px"
              >
                <option value="SUCCESS">성공</option>
                <option value="FAQ">FAQ</option>
                <option value="ERROR">오류</option>
                <option value="NEEDS_IMPROVEMENT">개선 필요</option>
              </Select>
              <Select
                placeholder="건물 필터"
                value={buildingFilter}
                onChange={(e) => {
                  setBuildingFilter(e.target.value)
                  setCurrentPage(1)
                }}
                maxW="180px"
              >
                {buildings.map(building => (
                  <option key={building} value={building}>{building}</option>
                ))}
              </Select>
              {/* 🆕 v10.3: 검증 상태 필터 */}
              <Select
                placeholder="검증 상태"
                value={validationFilter}
                onChange={(e) => {
                  setValidationFilter(e.target.value)
                  setCurrentPage(1)
                }}
                maxW="150px"
              >
                {validationTypes.map(vt => (
                  <option key={vt.value} value={vt.value}>{vt.label}</option>
                ))}
              </Select>
              <Input
                type="date"
                placeholder="시작 날짜"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                  setCurrentPage(1)
                }}
                maxW="150px"
              />
              <Input
                type="date"
                placeholder="종료 날짜"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  setCurrentPage(1)
                }}
                maxW="150px"
              />
              <Button
                variant="outline"
                onClick={handleResetFilters}
                size="md"
              >
                필터 초기화
              </Button>
              {/* 🆕 v10.10: 제외 IP 토글 버튼 */}
              <Button
                leftIcon={<NotAllowedIcon />}
                variant={showExcludeIps ? 'solid' : 'outline'}
                colorScheme={excludeIps.trim() ? 'red' : 'gray'}
                size="md"
                onClick={() => setShowExcludeIps(!showExcludeIps)}
                title="특정 IP를 검색 결과에서 제외합니다"
              >
                제외 IP {excludeIps.trim() ? `(${excludeIps.split(/[\n,]/).filter(ip => ip.trim()).length})` : ''}
              </Button>
            </HStack>
            
            {/* 🆕 v10.10: 제외 IP 입력 영역 */}
            <Collapse in={showExcludeIps} animateOpacity>
              <Box mt={4} p={4} bg="red.50" borderRadius="md" border="1px" borderColor="red.200">
                <Text fontSize="sm" fontWeight="bold" color="red.600" mb={2}>
                  🚫 제외할 IP 주소 (줄바꿈 또는 쉼표로 구분)
                </Text>
                <Textarea
                  placeholder="예시:
127.0.0.1
118.47.140.18
122.136.188.132, 210.120.27.131"
                  value={excludeIps}
                  onChange={(e) => setExcludeIps(e.target.value)}
                  size="sm"
                  rows={4}
                  bg="white"
                  borderColor="red.300"
                  _focus={{ borderColor: 'red.500', boxShadow: '0 0 0 1px #E53E3E' }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  입력된 IP 주소들은 검색 결과에서 제외됩니다. 내부 테스트 IP 등을 제외할 때 유용합니다.
                </Text>
              </Box>
            </Collapse>
          </VStack>
        </CardBody>
      </Card>

      {/* 로그 목록 테이블 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={0}>
          {filteredLogs.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.500">표시할 로그가 없습니다.</Text>
            </Box>
          ) : (
            <>
              {/* 페이지네이션 - 상단 */}
              <HStack justify="space-between" p={4} flexWrap="wrap" spacing={4} borderBottom="1px" borderColor={borderColor}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                    페이지당 표시:
                  </Text>
                  <Select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    size="sm"
                    maxW="100px"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                    총 {totalItems}개 중 {((currentPage - 1) * pageSize + 1)}-{Math.min(currentPage * pageSize, totalItems)}개 표시
                  </Text>
                </HStack>
                {totalPages > 1 && (
                  <HStack spacing={1}>
                    {/* 첫 페이지 버튼 */}
                    <Button
                      aria-label="첫 페이지"
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      isDisabled={currentPage === 1}
                      onClick={() => handlePageChange(1)}
                      minW="40px"
                    >
                      ««
                    </Button>
                    {/* 이전 페이지 버튼 */}
                    <IconButton
                      aria-label="이전 페이지"
                      icon={<ChevronLeftIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      isDisabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                            {/* 페이지 번호 버튼들 */}
                            {getPageNumbers().map((page, index) => {
                              if (page === '...') {
                                return (
                                  <Text key={`ellipsis-${index}`} fontSize="sm" px={2} color="gray.500">
                                    ...
                                  </Text>
                                )
                              }
                              const pageNum = page as number
                              const isActive = pageNum === currentPage
                              return isActive ? (
                                <Button
                                  key={pageNum}
                                  size="sm"
                                  variant="outline"
                                  colorScheme="blue"
                                  onClick={() => handlePageChange(pageNum)}
                                  minW="40px"
                                  fontWeight="bold"
                                  cursor="default"
                                >
                                  {pageNum}
                                </Button>
                              ) : (
                                <Button
                                  key={pageNum}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handlePageChange(pageNum)}
                                  minW="40px"
                                  color="gray.600"
                                  _hover={{ bg: 'gray.100' }}
                                >
                                  {pageNum}
                                </Button>
                              )
                            })}
                    {/* 다음 페이지 버튼 */}
                    <IconButton
                      aria-label="다음 페이지"
                      icon={<ChevronRightIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      isDisabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                    {/* 마지막 페이지 버튼 */}
                    <Button
                      aria-label="마지막 페이지"
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      isDisabled={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                      minW="40px"
                    >
                      »»
                    </Button>
                  </HStack>
                )}
              </HStack>

              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>시간</Th>
                    <Th>질문</Th>
                    <Th>건물</Th>
                    <Th>상태</Th>
                    <Th>검증</Th>
                    <Th>신뢰도</Th>
                    <Th>처리시간</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredLogs.map((log) => (
                    <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                      <Td 
                        whiteSpace="nowrap" 
                        cursor="pointer" 
                        onClick={() => handleViewLog(log)}
                        _hover={{ color: 'blue.500', textDecoration: 'underline' }}
                      >
                        {log.created_at ? new Date(log.created_at).toLocaleString('ko-KR') : '-'}
                      </Td>
                      <Td 
                        maxW="300px" 
                        isTruncated 
                        cursor="pointer" 
                        onClick={() => handleViewLog(log)}
                        _hover={{ color: 'blue.500', textDecoration: 'underline' }}
                      >
                        {log.query_text || '-'}
                      </Td>
                      <Td>
                        {log.building && log.building !== '-' ? (
                          <Badge colorScheme="blue">{log.building}</Badge>
                        ) : (
                          <Text color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(log.rag_status, log.is_faq)}>
                          {getStatusText(log.rag_status, log.is_faq)}
                        </Badge>
                      </Td>
                      {/* 🆕 v10.3: 검증 상태 */}
                      <Td>
                        <Badge colorScheme={getValidationColor(log.validation_type)}>
                          {getValidationText(log.validation_type)}
                        </Badge>
                      </Td>
                      <Td>
                        {log.confidence ? (
                          <Text>{(log.confidence * 100).toFixed(1)}%</Text>
                        ) : (
                          <Text color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td>{log.processingTime || 0}ms</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </>
          )}
        </CardBody>
      </Card>

      {/* 로그 상세 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>쿼리 로그 상세</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLog && (
              <VStack spacing={5} align="stretch">
                {/* 기본 정보 */}
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      시간
                    </Text>
                    <Text>
                      {selectedLog.created_at 
                        ? new Date(selectedLog.created_at).toLocaleString('ko-KR')
                        : '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      사용자 ID
                    </Text>
                    <Text>{selectedLog.userId || 'anonymous'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      상태
                    </Text>
                    <Badge colorScheme={getStatusColor(selectedLog.rag_status, selectedLog.is_faq)}>
                      {getStatusText(selectedLog.rag_status, selectedLog.is_faq)}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      신뢰도
                    </Text>
                    <Text>
                      {selectedLog.confidence 
                        ? (selectedLog.confidence * 100).toFixed(1) + '%' 
                        : '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      처리시간
                    </Text>
                    <Text>{selectedLog.processingTime || 0}ms</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      건물
                    </Text>
                    {selectedLog.building && selectedLog.building !== '-' ? (
                      <Badge colorScheme="blue">{selectedLog.building}</Badge>
                    ) : (
                      <Text color="gray.400">-</Text>
                    )}
                  </Box>
                  {/* 🆕 v10.3: 검증 상태 */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      검증 상태
                    </Text>
                    <Badge colorScheme={getValidationColor(selectedLog.validation_type)}>
                      {getValidationText(selectedLog.validation_type)}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      IP 주소
                    </Text>
                    <Text fontSize="sm">{selectedLog.ip || '-'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      클라이언트 ID
                    </Text>
                    <Text fontSize="sm">{selectedLog.clientId || '-'}</Text>
                  </Box>
                  {/* 🆕 v10.8: 사용자 유형 (클라이언트 ID 옆) */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                      사용자 유형
                    </Text>
                    {selectedLog.user_type ? (
                      <Badge colorScheme={
                        selectedLog.user_type === 'digital-twin' ? 'purple' :
                        selectedLog.user_type === 'mobile' ? 'blue' :
                        selectedLog.user_type === '방문자' ? 'green' :
                        'gray'
                      } fontSize="sm">
                        {selectedLog.user_type === 'digital-twin' ? '🎮 디지털 트윈' :
                         selectedLog.user_type === 'mobile' ? '📱 모바일' :
                         selectedLog.user_type === 'default' ? '💻 기본' :
                         selectedLog.user_type}
                      </Badge>
                    ) : (
                      <Text color="gray.400">-</Text>
                    )}
                  </Box>
                </SimpleGrid>
                
                {/* 🆕 v10.3: 검증 사유 표시 */}
                {selectedLog.validation_reason && (
                  <Box mt={2} p={3} bg="orange.50" borderRadius="md" borderLeft="4px" borderColor="orange.500">
                    <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={1}>
                      검증 사유
                    </Text>
                    <Text fontSize="sm">{selectedLog.validation_reason}</Text>
                  </Box>
                )}
                
                {/* 🆕 v10.7: 추출된 키워드 표시 */}
                {selectedLog.keyword && (
                  <Box mt={2} p={3} bg="teal.50" borderRadius="md" borderLeft="4px" borderColor="teal.500">
                    <Text fontSize="sm" fontWeight="bold" color="teal.600" mb={1}>
                      추출된 키워드
                    </Text>
                    <Text fontSize="sm" wordBreak="break-word">{selectedLog.keyword}</Text>
                  </Box>
                )}
                
                <Divider />
                
                {/* 질문 */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>
                    질문
                  </Text>
                  <Box p={4} bg="blue.50" borderRadius="md" borderLeft="4px" borderColor="blue.500">
                    <Text whiteSpace="pre-wrap">
                      {selectedLog.query_text || '질문 내용이 없습니다.'}
                    </Text>
                  </Box>
                </Box>
                
                {/* 답변 - HTML 렌더링 + CSS 적용 */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>
                    답변
                  </Text>
                  <Box 
                    p={4} 
                    bg="gray.50" 
                    borderRadius="md" 
                    borderLeft="4px" 
                    borderColor="gray.400"
                    maxH="400px"
                    overflowY="auto"
                    sx={{
                      // 챗봇 답변 스타일 (사용자 페이지와 동일)
                      '& h3': { 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        marginTop: '16px', 
                        marginBottom: '8px',
                        color: 'blue.700'
                      },
                      '& h4': { 
                        fontSize: '15px', 
                        fontWeight: 'bold', 
                        marginTop: '12px', 
                        marginBottom: '6px',
                        color: 'blue.600'
                      },
                      '& ul': { 
                        paddingLeft: '20px', 
                        marginTop: '8px', 
                        marginBottom: '8px',
                        listStyleType: 'disc'
                      },
                      '& ol': { 
                        paddingLeft: '20px', 
                        marginTop: '8px', 
                        marginBottom: '8px',
                        listStyleType: 'decimal'
                      },
                      '& li': { 
                        marginBottom: '4px',
                        lineHeight: '1.6'
                      },
                      '& p': { 
                        marginBottom: '8px',
                        lineHeight: '1.7'
                      },
                      '& strong, & b': { 
                        fontWeight: 'bold',
                        color: 'gray.800'
                      },
                      '& a': { 
                        color: 'blue.500', 
                        textDecoration: 'underline',
                        _hover: { color: 'blue.700' }
                      },
                      '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginTop: '8px',
                        marginBottom: '8px',
                        fontSize: '14px'
                      },
                      '& th, & td': {
                        border: '1px solid',
                        borderColor: 'gray.300',
                        padding: '8px',
                        textAlign: 'left'
                      },
                      '& th': {
                        backgroundColor: 'gray.100',
                        fontWeight: 'bold'
                      },
                      '& tr:nth-of-type(even)': {
                        backgroundColor: 'gray.50'
                      },
                      '& br': {
                        display: 'block',
                        marginBottom: '4px'
                      },
                      '& .emoji': {
                        fontSize: '18px'
                      }
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: selectedLog.response_text || '<p style="color: gray;">답변 내용이 없습니다.</p>' 
                    }}
                  />
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3} flexWrap="wrap" justifyContent="space-between" w="100%">
              {/* 🆕 v10.3: 검증 확정 버튼 그룹 */}
              <HStack spacing={2}>
                <Button
                  leftIcon={<CheckIcon />}
                  colorScheme="green"
                  size="sm"
                  onClick={() => selectedLog && handleConfirmAsVerified(selectedLog)}
                  title="정상 답변으로 확정하고 검증된 QA로 등록"
                >
                  정상 확정
                </Button>
                <Button
                  leftIcon={<CloseIcon />}
                  colorScheme="red"
                  size="sm"
                  variant="outline"
                  onClick={() => selectedLog && handleMarkAsWrong(selectedLog)}
                  title="오답으로 마킹"
                >
                  오답
                </Button>
              </HStack>
              
              {/* 닫기 버튼 */}
              <Button variant="ghost" size="sm" onClick={onClose}>
                닫기
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}

export default QueryLogsPage






